import { useRef, useEffect, useState, useMemo, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Stack,
  Button,
  IconButton,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
  CircularProgress,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import CloseIcon from "@mui/icons-material/Close";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import download from "downloadjs";

import bg1 from "@/images/storyBackground/1.png";
import bg2 from "@/images/storyBackground/2.png";
import bg3 from "@/images/storyBackground/3.png";
import bg4 from "@/images/storyBackground/4.png";
import bg5 from "@/images/storyBackground/5.png";
import bg6 from "@/images/storyBackground/6.png";
import vittaLight from "@/images/VittaLightMode.png";
import vittaDark from "@/images/VittaDarkMode.png";
import apeakClub from "@/images/apeak_club.png";

const BACKGROUNDS = [bg1, bg2, bg3, bg4, bg5, bg6];
const CANVAS_W = 1080;
const CANVAS_H = 1920;
const PREVIEW_W = 270;
const PREVIEW_H = Math.round(PREVIEW_W * (CANVAS_H / CANVAS_W));

const TYPE_LABELS = { MASC: "Masculino", FEM: "Feminino", MISTA: "Mista" };
const MEDAL_COLORS = { 1: "#FFD700", 2: "#C0C0C0", 3: "#CD7F32" };

const FONTS = [
  { value: "Impact", label: "Impact" },
  { value: '"Arial Black"', label: "Arial Black" },
  { value: "Georgia", label: "Georgia" },
  { value: "Verdana", label: "Verdana" },
  { value: '"Trebuchet MS"', label: "Trebuchet MS" },
];

// Loads assets without tainting the canvas (backgrounds, logos, download avatars).
// Uses crossOrigin + cache-bust first to avoid using a cached non-CORS response
// from the preview phase (which loads without crossOrigin and poisons the cache).
// Falls back to fetch+blob as secondary attempt.
async function loadImage(src) {
  if (!src) return null;

  // Cache-busting ensures a fresh request with CORS headers, bypassing any
  // non-CORS entry the preview phase may have stored in the browser cache.
  const cacheBust = `${src}${src.includes("?") ? "&" : "?"}_t=${Date.now()}`;

  const corsImg = await new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = cacheBust;
  });

  if (corsImg) return corsImg;

  // Fallback: fetch+blob bypasses the image-request cache entirely
  try {
    const res = await fetch(src, { cache: "no-store" });
    if (!res.ok) throw new Error("fetch failed");
    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        URL.revokeObjectURL(objectUrl);
        resolve(img);
      };
      img.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        resolve(null);
      };
      img.src = objectUrl;
    });
  } catch {
    return null;
  }
}

// Loads avatar images for the preview canvas only.
// Skips CORS so the photo always displays, at the cost of tainting the preview canvas
// (acceptable — we never call toBlob on the preview canvas).
function loadAvatarPreview(src) {
  if (!src) return Promise.resolve(null);
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

async function renderStory(
  canvas,
  {
    bgSrc,
    logoSrc,
    apeakLogoSrc,
    titleText,
    titleColor,
    titleFont,
    textTheme,
    players,
    pageOffset,
    categoryObj,
  },
  previewMode = false,
) {
  const ctx = canvas.getContext("2d");
  canvas.width = CANVAS_W;
  canvas.height = CANVAS_H;

  const isLight = textTheme === "light";
  const textColor = isLight ? "#FFFFFF" : "#1C1C1E";
  const subColor = isLight ? "rgba(255,255,255,0.72)" : "rgba(0,0,0,0.52)";
  const divColor = isLight ? "rgba(255,255,255,0.18)" : "rgba(0,0,0,0.10)";
  const accentGreen = "#2ECC71";
  const PAD = 80;

  const loadAvatar = previewMode ? loadAvatarPreview : loadImage;
  const [bgImg, logoImg, apeakLogoImg, ...avatarImgs] = await Promise.all([
    loadImage(bgSrc),
    loadImage(logoSrc),
    loadImage(apeakLogoSrc),
    ...players.map((p) => loadAvatar(p.avatarUrl)),
  ]);

  // Background
  if (bgImg) {
    ctx.drawImage(bgImg, 0, 0, CANVAS_W, CANVAS_H);
  } else {
    ctx.fillStyle = "#1a1a2e";
    ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);
  }

  const shadow = (on) => {
    if (on) {
      ctx.shadowColor = "rgba(0,0,0,0.45)";
      ctx.shadowBlur = 14;
      ctx.shadowOffsetY = 3;
    } else {
      ctx.shadowColor = "transparent";
      ctx.shadowBlur = 0;
      ctx.shadowOffsetY = 0;
    }
  };

  // ── HEADER ──────────────────────────────────────────────────
  const PAD_TOP = 80;

  const LOGO_MAX_H = 110;
  let logoX = PAD;

  if (apeakLogoImg) {
    const scale = Math.min(
      180 / apeakLogoImg.width,
      LOGO_MAX_H / apeakLogoImg.height,
    );
    const w = apeakLogoImg.width * scale;
    const h = apeakLogoImg.height * scale;
    shadow(true);
    ctx.drawImage(apeakLogoImg, logoX, PAD_TOP + (LOGO_MAX_H - h) / 2, w, h);
    shadow(false);
    logoX += w + 20;

    // divider between logos
    ctx.globalAlpha = 0.35;
    ctx.fillStyle = isLight ? "#ffffff" : "#000000";
    ctx.fillRect(logoX, PAD_TOP + 12, 2, LOGO_MAX_H - 24);
    ctx.globalAlpha = 1;
    logoX += 22;
  }

  if (logoImg) {
    const scale = Math.min(240 / logoImg.width, LOGO_MAX_H / logoImg.height);
    const w = logoImg.width * scale;
    const h = logoImg.height * scale;
    shadow(true);
    ctx.drawImage(logoImg, logoX, PAD_TOP + (LOGO_MAX_H - h) / 2, w, h);
    shadow(false);
  }

  ctx.textAlign = "right";
  ctx.textBaseline = "alphabetic";
  shadow(true);
  ctx.font = "44px Arial, sans-serif";
  ctx.fillStyle = subColor;
  ctx.fillText("Resultado Ranking", CANVAS_W - PAD, PAD_TOP + 60);

  ctx.font = `bold 92px ${titleFont}, sans-serif`;
  ctx.fillStyle = titleColor;
  ctx.fillText(titleText || "Ranking", CANVAS_W - PAD, PAD_TOP + 170);
  shadow(false);

  // Separator
  ctx.beginPath();
  ctx.moveTo(PAD, 310);
  ctx.lineTo(CANVAS_W - PAD, 310);
  ctx.strokeStyle = divColor;
  ctx.lineWidth = 2;
  ctx.stroke();

  // ── CATEGORY ────────────────────────────────────────────────
  const catLabel = "Categoria: ";
  const catName = categoryObj?.name ?? "";
  const catType = categoryObj?.type
    ? (TYPE_LABELS[categoryObj.type] ?? categoryObj.type)
    : "";
  const CAT_Y = 415;

  ctx.textAlign = "left";
  ctx.textBaseline = "alphabetic";
  ctx.font = "bold 72px Arial, sans-serif";
  shadow(true);

  ctx.fillStyle = textColor;
  ctx.fillText(catLabel, PAD, CAT_Y);
  const catLabelW = ctx.measureText(catLabel).width;

  if (catName) {
    ctx.fillStyle = titleColor;
    ctx.fillText(catName, PAD + catLabelW, CAT_Y);
    const catNameW = ctx.measureText(catName).width;

    ctx.fillStyle = accentGreen;
    ctx.font = "56px Arial, sans-serif";
    ctx.fillText(" " + catType, PAD + catLabelW + catNameW, CAT_Y);
  }
  shadow(false);

  // ── COLUMN HEADERS ──────────────────────────────────────────
  const HDR_Y = 508;
  ctx.font = "bold 40px Arial, sans-serif";
  ctx.fillStyle = accentGreen;
  ctx.textAlign = "left";
  ctx.fillText("Posição", PAD, HDR_Y);
  ctx.fillText("Jogador", PAD + 225, HDR_Y);

  ctx.beginPath();
  ctx.moveTo(PAD, HDR_Y + 22);
  ctx.lineTo(CANVAS_W - PAD, HDR_Y + 22);
  ctx.strokeStyle = accentGreen + "AA";
  ctx.lineWidth = 2;
  ctx.stroke();

  // ── PLAYER ROWS ─────────────────────────────────────────────
  const ROW_H = 200;
  const ROW_START = 578;
  const AVATAR_R = 62;
  const AVATAR_CX = PAD + 185;

  for (let i = 0; i < players.length; i++) {
    const player = players[i];
    const pos = pageOffset + i + 1;
    const rowY = ROW_START + i * ROW_H;
    const midY = rowY + ROW_H / 2;
    const avatarImg = avatarImgs[i] ?? null;
    const posColor = MEDAL_COLORS[pos] ?? textColor;

    // Position number
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.font = "bold 74px Arial, sans-serif";
    ctx.fillStyle = posColor;
    shadow(true);
    ctx.fillText(`${pos}º`, PAD + 60, midY);
    shadow(false);

    // Avatar background circle
    ctx.beginPath();
    ctx.arc(AVATAR_CX, midY, AVATAR_R, 0, Math.PI * 2);
    ctx.fillStyle =
      MEDAL_COLORS[pos] ??
      (isLight ? "rgba(255,255,255,0.18)" : "rgba(0,0,0,0.10)");
    ctx.fill();

    if (avatarImg) {
      ctx.save();
      ctx.beginPath();
      ctx.arc(AVATAR_CX, midY, AVATAR_R, 0, Math.PI * 2);
      ctx.clip();
      ctx.drawImage(
        avatarImg,
        AVATAR_CX - AVATAR_R,
        midY - AVATAR_R,
        AVATAR_R * 2,
        AVATAR_R * 2,
      );
      ctx.restore();
    } else {
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.font = `bold ${AVATAR_R}px Arial, sans-serif`;
      ctx.fillStyle = "#fff";
      ctx.fillText(player.name[0]?.toUpperCase() ?? "?", AVATAR_CX, midY);
    }

    // Avatar border
    ctx.beginPath();
    ctx.arc(AVATAR_CX, midY, AVATAR_R, 0, Math.PI * 2);
    ctx.strokeStyle =
      MEDAL_COLORS[pos] ??
      (isLight ? "rgba(255,255,255,0.35)" : "rgba(0,0,0,0.2)");
    ctx.lineWidth = pos <= 3 ? 5 : 3;
    ctx.stroke();

    // Player name
    const nameX = PAD + 275;
    const maxNameW = CANVAS_W - PAD - nameX - 230;
    ctx.font = "bold 48px Arial, sans-serif";
    ctx.fillStyle = textColor;
    ctx.textAlign = "left";
    ctx.textBaseline = "middle";
    shadow(true);
    let displayName = player.name;
    while (
      ctx.measureText(displayName).width > maxNameW &&
      displayName.length > 3
    ) {
      displayName = displayName.slice(0, -1);
    }
    if (displayName.length < player.name.length) displayName += "…";
    ctx.fillText(displayName, nameX, midY);

    // Points
    ctx.textAlign = "right";
    ctx.font = "bold 54px Arial, sans-serif";
    ctx.fillStyle = subColor;
    ctx.fillText(`${player.points} pts`, CANVAS_W - PAD, midY);
    shadow(false);

    // Row divider
    if (i < players.length - 1) {
      ctx.beginPath();
      ctx.moveTo(PAD + 120, rowY + ROW_H);
      ctx.lineTo(CANVAS_W - PAD, rowY + ROW_H);
      ctx.strokeStyle = divColor;
      ctx.lineWidth = 1.5;
      ctx.stroke();
    }
  }

  ctx.textBaseline = "alphabetic";
  ctx.textAlign = "left";
  ctx.shadowColor = "transparent";
  ctx.shadowBlur = 0;
  ctx.shadowOffsetY = 0;
}

export default function StoryExportModal({
  open,
  onClose,
  standings = [],
  category,
  categories = [],
  rankingName = "",
}) {
  const canvasRef = useRef(null);
  const offscreenRef = useRef(null);

  const [bgIdx, setBgIdx] = useState(0);
  const [logoMode, setLogoMode] = useState("light");
  const [titleText, setTitleText] = useState("");
  const [titleColor, setTitleColor] = useState("#FF6B35");
  const [titleFont, setTitleFont] = useState("Impact");
  const [textTheme, setTextTheme] = useState("light");
  const [currentPage, setCurrentPage] = useState(0);
  const [rendering, setRendering] = useState(false);

  useEffect(() => {
    if (rankingName && !titleText) setTitleText(rankingName);
  }, [rankingName]);

  // Reset page when standings change
  useEffect(() => {
    setCurrentPage(0);
  }, [standings]);

  const pages = useMemo(() => {
    if (!standings.length) return [[]];
    const chunks = [];
    for (let i = 0; i < standings.length; i += 5)
      chunks.push(standings.slice(i, i + 5));
    return chunks;
  }, [standings]);

  const totalPages = pages.length;
  const categoryObj = useMemo(
    () => categories.find((c) => c.id === category),
    [categories, category],
  );

  const buildOpts = useCallback(
    (page) => ({
      bgSrc: BACKGROUNDS[bgIdx],
      logoSrc: logoMode === "light" ? vittaLight : vittaDark,
      apeakLogoSrc: apeakClub,
      titleText: titleText || rankingName || "Ranking",
      titleColor,
      titleFont,
      textTheme,
      players: pages[page] ?? [],
      pageOffset: page * 5,
      categoryObj,
    }),
    [
      bgIdx,
      logoMode,
      titleText,
      rankingName,
      titleColor,
      titleFont,
      textTheme,
      pages,
      categoryObj,
    ],
  );

  // Re-render preview whenever options or page change
  useEffect(() => {
    if (!open) return;
    const canvas = canvasRef.current;
    if (!canvas) return;

    if (!offscreenRef.current) {
      offscreenRef.current = document.createElement("canvas");
    }
    const offscreen = offscreenRef.current;
    let cancelled = false;
    setRendering(true);

    renderStory(offscreen, buildOpts(currentPage), true)
      .then(() => {
        if (cancelled) return;
        const previewCtx = canvas.getContext("2d");
        canvas.width = PREVIEW_W;
        canvas.height = PREVIEW_H;
        previewCtx.drawImage(offscreen, 0, 0, PREVIEW_W, PREVIEW_H);
        setRendering(false);
      })
      .catch(() => {
        if (!cancelled) setRendering(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, currentPage, buildOpts]);

  const handleDownload = async () => {
    setRendering(true);
    try {
      for (let p = 0; p < totalPages; p++) {
        const canvas = document.createElement("canvas");
        await renderStory(canvas, buildOpts(p));
        const blob = await new Promise((resolve) =>
          canvas.toBlob(resolve, "image/png"),
        );
        download(blob, `story-ranking-pagina${p + 1}.png`, "image/png");
        if (p < totalPages - 1) await new Promise((r) => setTimeout(r, 400));
      }
    } finally {
      setRendering(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
    >
      <DialogContent
        sx={{ p: 0, display: "flex", height: "90vh", maxHeight: 860 }}
      >
        {/* ── Left: preview ── */}
        <Box
          sx={{
            width: PREVIEW_W + 48,
            flexShrink: 0,
            bgcolor: "#111827",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
            p: 3,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: "#9ca3af",
              letterSpacing: 1.5,
              fontSize: 10,
              textTransform: "uppercase",
            }}
          >
            Preview
          </Typography>

          <Box
            sx={{
              position: "relative",
              borderRadius: 2,
              overflow: "hidden",
              border: "1px solid rgba(255,255,255,0.1)",
              boxShadow: "0 8px 32px rgba(0,0,0,0.5)",
            }}
          >
            <canvas
              ref={canvasRef}
              style={{ display: "block", width: PREVIEW_W, height: PREVIEW_H }}
            />
            {rendering && (
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  bgcolor: "rgba(0,0,0,0.45)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <CircularProgress size={28} sx={{ color: "#fff" }} />
              </Box>
            )}
          </Box>

          {totalPages > 1 && (
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <IconButton
                size="small"
                onClick={() => setCurrentPage((p) => Math.max(0, p - 1))}
                disabled={currentPage === 0}
                sx={{
                  color: "#9ca3af",
                  "&.Mui-disabled": { color: "#374151" },
                }}
              >
                <NavigateBeforeIcon fontSize="small" />
              </IconButton>
              <Typography
                variant="caption"
                sx={{ color: "#9ca3af", minWidth: 50, textAlign: "center" }}
              >
                {currentPage + 1} / {totalPages}
              </Typography>
              <IconButton
                size="small"
                onClick={() =>
                  setCurrentPage((p) => Math.min(totalPages - 1, p + 1))
                }
                disabled={currentPage === totalPages - 1}
                sx={{
                  color: "#9ca3af",
                  "&.Mui-disabled": { color: "#374151" },
                }}
              >
                <NavigateNextIcon fontSize="small" />
              </IconButton>
            </Stack>
          )}
        </Box>

        {/* ── Right: controls ── */}
        <Box sx={{ flex: 1, overflow: "auto", p: 3 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
            mb={2.5}
          >
            <Box>
              <Typography variant="h6" fontWeight={800}>
                Exportar para Stories
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Customize e exporte o ranking em formato 9:16
              </Typography>
            </Box>
            <IconButton size="small" onClick={onClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>

          <Stack spacing={2.5} divider={<Divider />}>
            {/* Background */}
            <Box>
              <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                Background
              </Typography>
              <Stack direction="row" spacing={1}>
                {BACKGROUNDS.map((bg, i) => (
                  <Box
                    key={i}
                    onClick={() => setBgIdx(i)}
                    sx={{
                      width: 62,
                      height: 110,
                      borderRadius: 1.5,
                      overflow: "hidden",
                      border: "3px solid",
                      borderColor: bgIdx === i ? "primary.main" : "divider",
                      cursor: "pointer",
                      transition: "border-color 0.15s",
                      "&:hover": {
                        borderColor:
                          bgIdx === i ? "primary.main" : "action.focus",
                      },
                    }}
                  >
                    <img
                      src={bg}
                      alt={`BG ${i + 1}`}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        display: "block",
                      }}
                    />
                  </Box>
                ))}
              </Stack>
            </Box>

            {/* Logo */}
            <Box>
              <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                Logo
              </Typography>
              <Stack direction="row" spacing={1.5}>
                {[
                  {
                    mode: "light",
                    src: vittaLight,
                    bg: "#ffffff",
                    textCol: "#444",
                    label: "Claro",
                  },
                  {
                    mode: "dark",
                    src: vittaDark,
                    bg: "#1a1a1a",
                    textCol: "#999",
                    label: "Escuro",
                  },
                ].map(({ mode, src, bg, textCol, label }) => (
                  <Box
                    key={mode}
                    onClick={() => setLogoMode(mode)}
                    sx={{
                      px: 2.5,
                      py: 1.5,
                      bgcolor: bg,
                      borderRadius: 2,
                      border: "2px solid",
                      borderColor:
                        logoMode === mode ? "primary.main" : "divider",
                      cursor: "pointer",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 0.75,
                      transition: "border-color 0.15s",
                    }}
                  >
                    <img
                      src={src}
                      alt={label}
                      style={{
                        height: 28,
                        objectFit: "contain",
                        maxWidth: 120,
                      }}
                    />
                    <Typography
                      variant="caption"
                      sx={{ color: textCol, fontSize: 11 }}
                    >
                      {label}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Box>

            {/* Title */}
            <Box>
              <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                Título
              </Typography>
              <Stack spacing={1.5}>
                <TextField
                  size="small"
                  label="Texto do título"
                  value={titleText}
                  onChange={(e) => setTitleText(e.target.value)}
                  placeholder={rankingName || "Ex: Rodada 01"}
                  fullWidth
                />
                <Stack direction="row" spacing={1.5} alignItems="flex-end">
                  <Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      mb={0.5}
                    >
                      Cor
                    </Typography>
                    <Box
                      component="input"
                      type="color"
                      value={titleColor}
                      onChange={(e) => setTitleColor(e.target.value)}
                      sx={{
                        width: 52,
                        height: 40,
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1,
                        cursor: "pointer",
                        p: "3px",
                        bgcolor: "background.paper",
                      }}
                    />
                  </Box>
                  <FormControl size="small" sx={{ flex: 1 }}>
                    <InputLabel>Fonte</InputLabel>
                    <Select
                      value={titleFont}
                      label="Fonte"
                      onChange={(e) => setTitleFont(e.target.value)}
                    >
                      {FONTS.map((f) => (
                        <MenuItem
                          key={f.value}
                          value={f.value}
                          sx={{ fontFamily: f.value }}
                        >
                          {f.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Stack>
              </Stack>
            </Box>

            {/* Text theme */}
            <Box>
              <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                Tema do texto
              </Typography>
              <ToggleButtonGroup
                value={textTheme}
                exclusive
                onChange={(_, v) => v && setTextTheme(v)}
                size="small"
              >
                <ToggleButton value="light" sx={{ px: 2.5 }}>
                  Claro (branco)
                </ToggleButton>
                <ToggleButton value="dark" sx={{ px: 2.5 }}>
                  Escuro (preto)
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {/* Download */}
            <Box>
              <Button
                variant="contained"
                size="large"
                fullWidth
                startIcon={
                  rendering ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    <DownloadIcon />
                  )
                }
                onClick={handleDownload}
                disabled={rendering || !standings.length}
                sx={{ borderRadius: 2, fontWeight: 700, py: 1.3 }}
              >
                {rendering
                  ? "Gerando..."
                  : totalPages > 1
                    ? `Baixar ${totalPages} imagens`
                    : "Baixar imagem"}
              </Button>
              {totalPages > 1 && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  mt={1}
                  textAlign="center"
                >
                  {totalPages} imagens serão baixadas automaticamente
                </Typography>
              )}
            </Box>
          </Stack>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
